import { DrObject } from './dr-object';
import { Timezone } from '../models/timezone';
import { isNullOrUndefined } from './utils';

export enum AutoProjectCreation {
    Digital_Reef_Only = 'Digital_Reef_Only',
    Digital_Reef_and_Relativity = 'Digital_Reef_and_Relativity',
    Digital_Reef_and_Reef_Review = 'Digital_Reef_and_Reef_Review'
}

export enum DefaultProcessingOption {
    Import_into_Digital_Reef_Only = 'Import_into_Digital_Reef_Only',
    Import_into_Digital_Reef_and_Export_to_Selected_Review_Tool = 'Import_into_Digital_Reef_and_Export_to_Selected_Review_Tool'
}

export enum AvailableProjectSetupOptions {
    DIGITAL_REEF_ONLY = 'DIGITAL_REEF_ONLY',
    DIGITAL_REEF_AND_RELATIVITY = 'DIGITAL_REEF_AND_RELATIVITY',
    DIGITAL_REEF_AND_REEF_REVIEW = 'DIGITAL_REEF_AND_REEF_REVIEW'
}

export class Preferences {
    // Project settings preferences
    dashboardReportsEnabled: boolean;
    drTimezone: string;
    enableKeyboardShorcuts: boolean;
    htmlConversionCacheEnabled: boolean;
    name: string;
    ownerId: string;
    preferenceId: number;
    recipients: Array<string>;
    reportDeliveryEnabled: boolean;
    suppressDuplicates: boolean;
    suppressArchivedNavTree: boolean;
    lastAccessTimeForArchival: { value: number };

    // Organization settings preferences
    autoProjectCreation: AutoProjectCreation;
    defaultProcessingOption: DefaultProcessingOption;
    availableProjectSetupOptions: Array<AvailableProjectSetupOptions>;

    constructor(value?: Partial<Preferences>) {
        if (value) {
            Object.assign(this, value);
        }
    }
}

export class PreferencesViewModel {

    model: Partial<Preferences>;
    original: Partial<Preferences>;

    public selectedTimezone: Timezone;

    constructor(value?: Partial<Preferences>) {
        if (value) {
            this.model = new Preferences(value);
            this.selectedTimezone = null;
        }

        if (this.model) {
            // no deep copy of recipients since their dirty state is managed by key-value component.
            this.original = new Preferences(this.model);
            this.original.lastAccessTimeForArchival = { value: this.model.lastAccessTimeForArchival?.value };
        }
    }

    isDirty(): boolean {
        // Project settings preferences
        if (this.model.dashboardReportsEnabled !== this.original.dashboardReportsEnabled) {
            return true;
        }

        if (this.selectedTimezone && DrObject.isValueDirty(this.selectedTimezone.id, this.original.drTimezone)) {
            this.model.drTimezone = this.selectedTimezone.id;
            return true;
        }

        if (this.model.enableKeyboardShorcuts !== this.original.enableKeyboardShorcuts) {
            return true;
        }

        if (this.model.htmlConversionCacheEnabled !== this.original.htmlConversionCacheEnabled) {
            return true;
        }

        if (DrObject.isValueDirty(this.model.name, this.original.name)) {
            return true;
        }

        if (DrObject.isValueDirty(this.model.ownerId, this.original.ownerId)) {
            return true;
        }

        if (this.model.reportDeliveryEnabled !== this.original.reportDeliveryEnabled) {
            return true;
        }

        if (this.model.suppressDuplicates !== this.original.suppressDuplicates) {
            return true;
        }

        if (this.model.suppressArchivedNavTree !== this.original.suppressArchivedNavTree) {
            return true;
        }

        if (this.model.lastAccessTimeForArchival?.value && this.model.lastAccessTimeForArchival.value !== this.original.lastAccessTimeForArchival.value) {
            return true;
        }

        // Organization settings preferences
        if (this.model.autoProjectCreation !== this.original.autoProjectCreation) {
            return true;
        }

        if (this.model.defaultProcessingOption !== this.original.defaultProcessingOption) {
            return true;
        }

        if ((!isNullOrUndefined(this.model.availableProjectSetupOptions) || !isNullOrUndefined(this.original.availableProjectSetupOptions))) {
            if (this.model.availableProjectSetupOptions.length !== this.original.availableProjectSetupOptions.length) {
                return true;
            }
            else {
                for (let i = 0; i < this.original.availableProjectSetupOptions.length; i++) {
                    if (!this.original.availableProjectSetupOptions.find(x => this.model.availableProjectSetupOptions[i])) {
                        return true;
                    }
                }
            }
        }
        // recipients: Array<string> // this object's dirty state is handled by the key-value component
        // on the Email Delivery settings.

        return false;
    }
}
